import { API } from "@/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";


// Log API base for debugging

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get("token");
      const error = searchParams.get("error");

      if (error) {
        setStatus("error");
        toast.error(`Authentication failed: ${error.replace(/_/g, " ")}`);
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      if (!token) {
        setStatus("error");
        toast.error("No authentication token received");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      try {
        // Store the token
        localStorage.setItem("token", token);

        // Verify token and get user info
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setStatus("success");
        toast.success("Welcome to OuttaTouch!");
        
        // Navigate to dashboard with user data
        navigate("/dashboard", { 
          state: { user: response.data }, 
          replace: true 
        });
      } catch (error) {
        console.error("Auth callback error:", error);
        setStatus("error");
        localStorage.removeItem("token");
        toast.error("Authentication verification failed");
        setTimeout(() => navigate("/login"), 2000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <motion.div
        className="text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-16 h-16 bg-[#E07A5F] rounded-full flex items-center justify-center mx-auto mb-6">
          <Phone className="w-8 h-8 text-white" />
        </div>

        {status === "processing" && (
          <>
            <Loader2 className="w-8 h-8 text-[#E07A5F] animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#3D405B] mb-2 font-fraunces">
              Signing you in...
            </h1>
            <p className="text-[#6D6F7C]">Please wait while we complete your authentication.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-8 h-8 bg-[#81B29A] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#3D405B] mb-2 font-fraunces">
              Success!
            </h1>
            <p className="text-[#6D6F7C]">Redirecting to your dashboard...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#3D405B] mb-2 font-fraunces">
              Authentication Failed
            </h1>
            <p className="text-[#6D6F7C]">Redirecting to login...</p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AuthCallbackPage;
